
'use client';

import React, { createContext, useContext, ReactNode, useCallback, useState, useEffect } from 'react';
import { useActiveProject } from './active-project-context';
import { Project } from '@/domains/projects/schemas';
import { ProjectAgent, AgentUpdate } from '@/domains/agents';
import { useToast } from '@/hooks/use-toast';
import { rehydrateProjectState } from '@/domains/projects/services/project-service';
import { useProjectAgents } from '@/domains/agents/hooks/use-project-agents';
import { useAgentModelValidation } from '@/domains/agents/hooks/use-agent-model-validation';
import { InvalidAgentModelsDialog } from '@/domains/agents/components/invalid-agent-models-dialog';
import { useProjectCore } from '@/domains/projects/hooks/use-project-core';
import type { ComparisonSession } from '@/domains/writing-experience';


interface AgentActions {
  getAgent: (agentId: string) => ProjectAgent | undefined;
  updateAgent: (agentId: string, updates: AgentUpdate) => Promise<void>;
  setAllAgents: (agents: ProjectAgent[]) => Promise<void>;
}

export interface ProjectContextType extends AgentActions {
  project: Project;
  setProject: React.Dispatch<React.SetStateAction<Project>>;
  updateProjectCore: (updates: Partial<Project>) => Promise<void>;
  fetchAllResources: () => Promise<void>;
  
  // Prose Comparison Session
  comparisonSession: ComparisonSession | null;
  startComparisonSession: (session: ComparisonSession) => void;
  clearComparisonSession: () => void;
}

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
    initialProject: Project;
    children: ReactNode;
}

export function ProjectProvider({ initialProject, children }: ProjectProviderProps) {
  const [project, setProject] = useState<Project>(initialProject);
  const [comparisonSession, setComparisonSession] = useState<ComparisonSession | null>(null);
  const { setActiveProject } = useActiveProject();

  const projectCoreManager = useProjectCore(project, setProject);
  const agentManager = useProjectAgents(project, setProject);
  const { toast } = useToast();

  // Background validation: detect agents configured with models that no longer exist
  const modelValidation = useAgentModelValidation({
    agents: agentManager.agents,
    projectId: project.id,
    updateAgent: agentManager.updateAgent,
  });

  // Sync active project info to global context for header display
  useEffect(() => {
    setActiveProject({ id: project.id, title: project.title });

    // Clear active project when unmounting (leaving project)
    return () => {
      setActiveProject(null);
    };
  }, [project.id, project.title, setActiveProject]);

  const fetchAllResources = useCallback(async () => {
    try {
      const fullProjectState = await rehydrateProjectState(project.id);
      if (fullProjectState) {
        setProject(fullProjectState);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Refresh Failed',
        description: 'Could not re-sync project state from the server.'
      });
    }
  }, [project.id, toast]);

  const startComparisonSession = useCallback((session: ComparisonSession) => {
    setComparisonSession(session);
  }, []);

  const clearComparisonSession = useCallback(() => {
    setComparisonSession(null);
  }, []);

  const value: ProjectContextType = {
      project,
      setProject,
      ...projectCoreManager,
      ...agentManager,
      fetchAllResources,
      comparisonSession,
      startComparisonSession,
      clearComparisonSession,
  };
  
  return (
    <ProjectContext.Provider value={value as any}>
      {children}
      <InvalidAgentModelsDialog
        open={modelValidation.dialogOpen}
        invalidEntries={modelValidation.invalidEntries}
        onConfirm={modelValidation.handleConfirm}
        onDismiss={modelValidation.handleDismiss}
      />
    </ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextType {
  const projectContext = useContext(ProjectContext);
  
  if (projectContext === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }

  return projectContext;
}
